export const FEEDBACK_STATUS = {
  OPEN: 'Open',
  RESOLVED: 'Resolved',
  DISMISSED: 'Dismissed',
};

export const FEEDBACK_STATUS_VALUES = Object.values(FEEDBACK_STATUS);

export const FEEDBACK_CATEGORY = {
  APP_PERFORMANCE: 'AppPerformance',
  LOAN_APPLICATION_PROCESS: 'LoanApplicationProcess',
  REPAYMENTS: 'Repayments',
  TRANSFERS_AND_PAYMENTS: 'TransfersAndPayments',
  CUSTOMER_SUPPORT: 'CustomerSupport',
  ACCOUNT_AND_KYC: 'AccountAndKyc',
  OTHER: 'Other',
};

export const FEEDBACK_CATEGORY_VALUES = Object.values(FEEDBACK_CATEGORY);

export const FEEDBACK_TRIGGER_POINT = {
  LOAN_ACCEPTANCE: 'LoanAcceptance',
};

export const FEEDBACK_TRIGGER_POINT_VALUES = Object.values(FEEDBACK_TRIGGER_POINT);

export const FEEDBACK_PLATFORM = {
  WEB: 'Web',
  ANDROID: 'Android',
  IOS: 'iOS',
  KONGA_SHQ: 'KongaSHQ',
  BLUE_PAGES: 'BluePages',
  EMBEDDED: 'Embedded',
  UNKNOWN: 'Unknown',
};

export const FEEDBACK_PLATFORM_VALUES = Object.values(FEEDBACK_PLATFORM);

export const FEEDBACK_RATINGS = [1, 2, 3, 4, 5];

export const formatFeedbackEnum = (value) => {
  if (!value) return '—';
  return String(value).split(/(?=[A-Z])/).join(' ');
};

export const FEEDBACK_STATUS_LABELS = {
  [FEEDBACK_STATUS.OPEN]: 'Open',
  [FEEDBACK_STATUS.RESOLVED]: 'Resolved',
  [FEEDBACK_STATUS.DISMISSED]: 'Dismissed',
};

export const FEEDBACK_CATEGORY_LABELS = FEEDBACK_CATEGORY_VALUES.reduce((acc, value) => {
  acc[value] = formatFeedbackEnum(value);
  return acc;
}, {});

export const FEEDBACK_PLATFORM_LABELS = {
  [FEEDBACK_PLATFORM.WEB]: 'Web',
  [FEEDBACK_PLATFORM.ANDROID]: 'Android',
  [FEEDBACK_PLATFORM.IOS]: 'iOS',
  [FEEDBACK_PLATFORM.KONGA_SHQ]: 'Konga SHQ',
  [FEEDBACK_PLATFORM.BLUE_PAGES]: 'Blue Pages',
  [FEEDBACK_PLATFORM.EMBEDDED]: 'Embedded',
  [FEEDBACK_PLATFORM.UNKNOWN]: 'Unknown',
};

export const FEEDBACK_TRIGGER_POINT_LABELS = {
  [FEEDBACK_TRIGGER_POINT.LOAN_ACCEPTANCE]: 'Loan acceptance',
};

export const feedbackTableFields = [
  { key: 'customerName', label: 'Customer', sortable: true },
  { key: 'rating', label: 'Rating', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'platform', label: 'Platform', sortable: true },
  { key: 'triggerPoint', label: 'Trigger point', sortable: true },
  { key: 'createdAt', label: 'Date', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
];

export const feedbackExportHeaders = [
  { key: 'customerName', label: 'Customer' },
  { key: 'rating', label: 'Rating' },
  { key: 'category', label: 'Category' },
  { key: 'platform', label: 'Platform' },
  { key: 'triggerPoint', label: 'Trigger point' },
  { key: 'loanReference', label: 'Loan reference' },
  { key: 'createdAt', label: 'Date' },
  { key: 'status', label: 'Status' },
  { key: 'comment', label: 'Comment' },
];

const pick = (obj, keys, fallback = '') => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return fallback;
};

export const mapFeedbackItem = (item = {}) => {
  const customer =
    item.customer ||
    item.user ||
    item.customerProfile ||
    {};

  return {
    id: pick(item, ['id', 'feedbackId', 'appRatingId'], null),
    userId: pick(item, ['userId', 'customerId', 'customerUserId'], customer.id || null),
    customerName:
      pick(item, ['customerName', 'userName', 'fullName', 'name'], '') ||
      [customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
      customer.name ||
      '—',
    rating: Number(pick(item, ['rating', 'score', 'appRating'], 0)) || 0,
    category: pick(item, ['category'], ''),
    status: pick(item, ['status'], FEEDBACK_STATUS.OPEN),
    platform: pick(item, ['platform'], ''),
    triggerPoint: pick(item, ['triggerPoint', 'trigger_point'], ''),
    loanReference: pick(item, ['loanReference', 'loanRef', 'loanRequestRef', 'loanRequestReference'], ''),
    comment: pick(item, ['comment', 'feedbackPreview', 'feedback', 'feedbackComment', 'comments'], ''),
    createdAt: pick(item, ['date', 'createdAt', 'submittedAt', 'ratedAt', 'dateCreated', 'createdOn'], null),
    internalNotes: pick(item, ['internalNotes', 'resolutionComments', 'staffComments', 'reviewerComments'], ''),
  };
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const mapKeyedCounts = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source
      .map((entry) => ({
        key: entry.key || entry.name || entry.category || entry.label || entry.rating,
        label: formatFeedbackEnum(entry.key || entry.name || entry.category || entry.label || entry.rating),
        value: toNumber(entry.value ?? entry.count ?? entry.total),
      }))
      .filter((entry) => entry.key != null);
  }
  return Object.entries(source).map(([key, value]) => ({
    key,
    label: formatFeedbackEnum(key),
    value: toNumber(typeof value === 'object' ? value?.count ?? value?.total ?? value?.value : value),
  }));
};

export const mapFeedbackStats = (payload = {}) => {
  const src = payload.data && !Array.isArray(payload.data) ? payload.data : payload;

  const ratingDistribution = mapKeyedCounts(
    src.ratingDistribution || src.ratings || src.byRating || src.ratingBreakdown
  );
  const categoryBreakdown = mapKeyedCounts(
    src.categoryBreakdown || src.byCategory || src.categories || src.categoryDistribution
  );

  return {
    total: toNumber(
      src.totalFeedback ?? src.total ?? src.totalCount ?? src.count ?? src.numberOfRatings
    ),
    averageRating: toNumber(
      src.averageRating ?? src.avgRating ?? src.average ?? src.meanRating
    ),
    openCount: toNumber(src.openCount ?? src.open ?? src.byStatus?.Open ?? src.statusBreakdown?.Open),
    resolvedCount: toNumber(
      src.resolvedCount ?? src.resolved ?? src.byStatus?.Resolved ?? src.statusBreakdown?.Resolved
    ),
    dismissedCount: toNumber(
      src.dismissedCount ?? src.dismissed ?? src.byStatus?.Dismissed ?? src.statusBreakdown?.Dismissed
    ),
    ratingDistribution,
    categoryBreakdown,
  };
};

export const normalizeFeedbackListResponse = (axiosResponse) => {
  const body = axiosResponse?.data;
  const items = Array.isArray(body) ? body : body?.data ?? body?.items ?? body?.feedback ?? [];
  const pagination = body?.pagination || body?.meta || {};
  const mapped = (Array.isArray(items) ? items : []).map(mapFeedbackItem);

  return {
    data: mapped,
    pagination: {
      page: pagination.page || pagination.currentPage || 1,
      pageSize: pagination.pageSize || pagination.page_size || mapped.length || 25,
      total: pagination.total || pagination.totalCount || pagination.totalItems || mapped.length,
    },
  };
};

export const mapFeedbackUser = (payload = {}) => {
  const src = payload.data && !Array.isArray(payload.data) ? payload.data : payload;
  const profile = src.profile || src.customerProfile || {};
  const firstName = src.firstName || profile.firstName || '';
  const lastName = src.lastName || profile.lastName || '';
  const name =
    [firstName, lastName].filter(Boolean).join(' ') ||
    src.fullName ||
    src.name ||
    profile.name ||
    '—';

  return {
    id: src.id || src.userId || null,
    name,
    email: src.emailAddress || src.email || profile.emailAddress || '',
    phone: src.phoneNumber || src.phone || profile.phoneNumber || '',
    countryCode: src.countryCode || profile.countryCode || '',
  };
};

export const feedbackStatusBadgeVariant = (status) => {
  if (status === FEEDBACK_STATUS.RESOLVED) return 'success';
  if (status === FEEDBACK_STATUS.DISMISSED) return 'muted';
  return 'warning';
};
